import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

import BallotStatusDot from '@/components/ballot/BallotStatusDot';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import useAnalytics from '@/hooks/useAnalytics';
import { useDirection } from '@/hooks/useDirection';
import { BORDER_RADIUS, primitiveColors, semanticColors } from '@/theme/tokens';
import {
  getBallotDotState,
  hasUpcomingElection,
  type BallotRecordSummary,
} from '@/types/ballotRecords';
import BallotIcon from '@/ui-library/BallotIcon';
import { TOGGLE_SX } from '@/ui-library/switch.styles';
import { extraElectionCount, formatBallotRecordName } from '@/utils/ballotRecordName';
import { ltrValueProps } from '@/utils/bidi';
import AutoCompleteAddressInput from '../signup/AutoCompleteAddressInput';
import { useNotification } from '../ui-library/NotificationProvider';
import SaveStatus from './SaveStatus';
import SectionSaveRow from './SectionSaveRow';
import {
  SettingsRow,
  SettingsSection,
  useSettingsControlHeight,
  useSettingsSurface,
} from './SettingsSection';
import { FIELD_LABEL_SX, INPUT_SX } from './settingsStyles';
import type { SettingsPanelProps } from './settingsTypes';

const VISIBLE_BALLOT_ROWS = 5;

interface BallotSettingsSectionProps extends SettingsPanelProps {
  onBusyChange?: (busy: boolean) => void;
}

export default function BallotSettingsSection({
  settings,
  onBusyChange,
}: BallotSettingsSectionProps) {
  const { t, i18n } = useTranslation('settings');
  const { t: tBallot } = useTranslation('ballot');
  const analytics = useAnalytics();
  const dir = useDirection();
  const surface = useSettingsSurface();
  const controlHeight = useSettingsControlHeight();
  const { showNotification } = useNotification();
  const { records, activeBallotId, activateBallot, deleteRecord } = useBallotRecords();
  const visibleRecords = records.filter((record) => record.is_visible_entry !== false);
  // activeBallotId moves the moment a ballot is picked; is_active_entry waits for the
  // list refresh, so lead with the id whenever it names a visible row.
  const activeVisibleId = visibleRecords.some((record) => record.id === activeBallotId)
    ? activeBallotId
    : null;
  const [showAllBallots, setShowAllBallots] = useState(false);
  const [ballotToDelete, setBallotToDelete] = useState<BallotRecordSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAddressEditor, setShowAddressEditor] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [addressSubmitError, setAddressSubmitError] = useState<string | null>(null);
  const addressSavingRef = useRef(false);
  const addressEntryRef = useRef<HTMLButtonElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const restoreAddressEntryFocusRef = useRef(false);
  const deletingRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  const focusHeadingRef = useRef(false);
  const hadBallotsRef = useRef(false);
  const {
    address,
    addressError,
    addressAlreadySaved,
    autoSaveBallots,
    groups,
    handleAddressChange,
    handleAddressSelect,
    saveAddress,
    startNewAddress,
    cancelAddress,
    clearAddressField,
    toggleAutoSaveBallots,
  } = settings;
  const statusLabels = { savingLabel: t('saving'), savedLabel: t('saved') };
  const addressSaving = isSavingAddress || groups.address.state === 'saving';

  useEffect(() => {
    onBusyChange?.(isDeleting || isSavingAddress);
    return () => onBusyChange?.(false);
  }, [isDeleting, isSavingAddress, onBusyChange]);

  useEffect(() => {
    if (showAddressEditor) {
      addressInputRef.current?.focus({ preventScroll: true });
    } else if (restoreAddressEntryFocusRef.current) {
      addressEntryRef.current?.focus({ preventScroll: true });
      restoreAddressEntryFocusRef.current = false;
    }
  }, [showAddressEditor]);

  useEffect(() => {
    if (visibleRecords.length === 0 && hadBallotsRef.current) clearAddressField();
    hadBallotsRef.current = visibleRecords.length > 0;
  }, [visibleRecords.length, clearAddressField]);

  const confirmDeleteBallot = useCallback(async () => {
    if (!ballotToDelete || deletingRef.current) return;
    deletingRef.current = true;
    setIsDeleting(true);
    try {
      const deleted = await deleteRecord(ballotToDelete.id);
      if (!deleted) {
        showNotification(t('ballotDeleteFailed'), 'error');
        return;
      }
      focusHeadingRef.current = true;
      setBallotToDelete(null);
    } catch (error) {
      console.error('Error deleting ballot:', error);
      showNotification(t('ballotDeleteFailed'), 'error');
    } finally {
      deletingRef.current = false;
      setIsDeleting(false);
    }
  }, [ballotToDelete, deleteRecord, showNotification, t]);

  const closeDeleteDialog = () => {
    if (!deletingRef.current) setBallotToDelete(null);
  };

  const restoreDeleteFocus = () => {
    if (focusHeadingRef.current) {
      const focusTarget = surface === 'dialog' ? descriptionRef.current : headingRef.current;
      focusTarget?.scrollIntoView({ block: 'nearest' });
      focusTarget?.focus({ preventScroll: true });
      focusHeadingRef.current = false;
    } else if (deleteTriggerRef.current?.isConnected) {
      deleteTriggerRef.current.focus({ preventScroll: true });
    }
  };

  const closeAddressEditor = () => {
    if (addressSavingRef.current || addressSaving) return;
    cancelAddress();
    setAddressSubmitError(null);
    restoreAddressEntryFocusRef.current = true;
    setShowAddressEditor(false);
  };

  const submitAddress = async () => {
    if (addressSavingRef.current) return;
    addressSavingRef.current = true;
    setAddressSubmitError(null);
    setIsSavingAddress(true);
    try {
      const saved = await saveAddress();
      if (saved && showAddressEditor) {
        restoreAddressEntryFocusRef.current = true;
        setShowAddressEditor(false);
      }
    } catch (error) {
      console.error('Error saving address:', error);
      setAddressSubmitError(t('unableToSave'));
    } finally {
      addressSavingRef.current = false;
      setIsSavingAddress(false);
    }
  };

  const autoSaveControl = (
    <Switch
      checked={autoSaveBallots}
      onChange={(event) => toggleAutoSaveBallots(event.target.checked)}
      disabled={groups.autoSaveBallots.state === 'saving'}
      sx={TOGGLE_SX}
      slotProps={{
        input: {
          'aria-label': t('autoSaveBallots'),
          'aria-describedby': 'settings-auto-save-desc',
        },
      }}
    />
  );
  const autoSaveStatus = (
    <SaveStatus
      state={groups.autoSaveBallots.state}
      error={groups.autoSaveBallots.error}
      {...statusLabels}
    />
  );

  return (
    <>
      <SettingsSection>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
          {surface === 'page' && (
            <Box sx={{ mt: '2px', flexShrink: 0, lineHeight: 0 }}>
              <BallotIcon size={20} color={semanticColors.textPrimary} />
            </Box>
          )}
          <Box>
            {surface === 'page' && (
              <Typography
                ref={headingRef}
                id="voting-info-section-heading"
                component="h2"
                tabIndex={-1}
                sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3, outline: 'none' }}
              >
                {t('ballotsAndAddresses')}
              </Typography>
            )}
            <Typography
              component="p"
              ref={surface === 'dialog' ? descriptionRef : undefined}
              tabIndex={surface === 'dialog' ? -1 : undefined}
              sx={{
                fontSize: surface === 'dialog' ? '14px' : '13px',
                lineHeight: 1.5,
                color: semanticColors.textTertiary,
                mt: surface === 'page' ? 0.5 : 0,
                outline: 'none',
              }}
            >
              {t('ballotsAndAddressesDesc')}
            </Typography>
          </Box>
        </Box>

        {/* Adding an address leads the section; the saved ballots follow. */}
        {visibleRecords.length > 0 && !showAddressEditor && (
          <Button
            ref={addressEntryRef}
            startIcon={<AddIcon />}
            onClick={() => {
              startNewAddress();
              setAddressSubmitError(null);
              setShowAddressEditor(true);
              analytics.track('Settings Address Editor Opened', { surface });
            }}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              color: semanticColors.textPrimary,
              minHeight: 40,
              padding: '0 12px',
              '&:hover': { backgroundColor: semanticColors.overlaySelected },
            }}
          >
            {t('addAddress')}
          </Button>
        )}

        {(visibleRecords.length === 0 || showAddressEditor) && (
          <Box sx={{ py: 0.5 }}>
            <Typography
              component="label"
              htmlFor="settings-voting-address-input"
              sx={surface === 'dialog' ? { ...FIELD_LABEL_SX, fontSize: '14px' } : FIELD_LABEL_SX}
            >
              {t(showAddressEditor ? 'newVotingAddress' : 'registeredVotingAddress')}
            </Typography>
            <AutoCompleteAddressInput
              id="settings-voting-address-input"
              value={address}
              onChange={handleAddressChange}
              onAddressSelect={handleAddressSelect}
              ariaLabel={t(showAddressEditor ? 'newVotingAddress' : 'registeredVotingAddress')}
              placeholder={t('addressPlaceholder')}
              error={!!addressError}
              disabled={addressSaving}
              inputRef={addressInputRef}
              helperText={addressError}
              size="small"
              sx={INPUT_SX}
            />
            <SectionSaveRow
              dirty={groups.address.dirty}
              state={
                isSavingAddress ? 'saving' : addressSubmitError ? 'error' : groups.address.state
              }
              error={addressSubmitError ?? groups.address.error}
              canSave={groups.address.canSave}
              saveLabel={t('saveAddress')}
              cancelLabel={t('cancel')}
              blockedHint={t(
                addressAlreadySaved ? 'addressAlreadySaved' : 'selectSuggestionToSave'
              )}
              {...statusLabels}
              onSave={() => void (showAddressEditor ? submitAddress() : saveAddress())}
              onCancel={showAddressEditor ? closeAddressEditor : cancelAddress}
            />
            {showAddressEditor && !groups.address.dirty && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button
                  onClick={closeAddressEditor}
                  disabled={addressSaving}
                  sx={{
                    textTransform: 'none',
                    color: semanticColors.textSecondary,
                    height: controlHeight,
                    padding: '0 12px',
                  }}
                >
                  {t('cancel')}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Stays mounted as the live region, but takes room only while it has
            something to report, so the list sits right under Add address. */}
        {visibleRecords.length > 0 && (
          <Box sx={{ mt: groups.address.state === 'idle' || showAddressEditor ? 0 : 0.5 }}>
            <SaveStatus
              state={showAddressEditor ? 'idle' : groups.address.state}
              error={groups.address.error}
              {...statusLabels}
            />
          </Box>
        )}

        {visibleRecords.length > 0 ? (
          <>
            <Box
              role="list"
              id="settings-ballot-list"
              aria-label={t('ballotsAndAddresses')}
              sx={{ mt: 0.5 }}
            >
              {(showAllBallots ? visibleRecords : visibleRecords.slice(0, VISIBLE_BALLOT_ROWS)).map(
                (record) => {
                  const name = formatBallotRecordName(
                    record,
                    i18n.language,
                    tBallot('records.unnamed')
                  );
                  const extra = extraElectionCount(record);
                  const isActive = activeVisibleId
                    ? record.id === activeVisibleId
                    : record.is_active_entry === true ||
                      (surface === 'page' && !activeBallotId && record.is_current_address);
                  return (
                    <Box
                      role="listitem"
                      key={record.id}
                      sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                    >
                      <ButtonBase
                        onClick={() => void activateBallot(record.id)}
                        disabled={addressSaving || showAddressEditor}
                        aria-label={tBallot('records.openAriaLabel', { name })}
                        aria-pressed={isActive}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: 1,
                          px: 1,
                          py: 1,
                          paddingInlineEnd: '44px',
                          textAlign: 'start',
                          borderRadius: BORDER_RADIUS,
                          backgroundColor: isActive
                            ? semanticColors.overlaySelected
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: isActive
                              ? semanticColors.overlaySelected
                              : semanticColors.overlaySubtle,
                          },
                          '&:focus-visible': {
                            outline: `2px solid ${primitiveColors.logoPurple}`,
                            outlineOffset: '2px',
                            zIndex: 1,
                          },
                        }}
                      >
                        <BallotStatusDot
                          state={getBallotDotState(
                            record.status,
                            hasUpcomingElection(record.elections),
                            record.is_complete
                          )}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              noWrap
                              sx={{
                                minWidth: 0,
                                maxWidth: '100%',
                                fontSize: '14px',
                                color: semanticColors.textBody,
                                fontWeight: isActive ? 600 : 400,
                              }}
                            >
                              {name}
                              {extra > 0 &&
                                ` ${tBallot('records.moreElections', { count: extra })}`}
                            </Typography>
                            {surface === 'dialog' && isActive && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  lineHeight: 1.5,
                                  color: semanticColors.textBrand,
                                  border: `1px solid ${semanticColors.borderLight}`,
                                  borderRadius: `calc(${BORDER_RADIUS} / 2)`,
                                  px: '6px',
                                  flexShrink: 0,
                                }}
                              >
                                {t('ballotCurrentlyInUse')}
                              </Typography>
                            )}
                          </Box>
                          {(record.address_full || record.location_label) && (
                            <Typography
                              noWrap
                              {...ltrValueProps(dir)}
                              sx={{
                                fontSize: surface === 'dialog' ? '13px' : '12px',
                                color: isActive
                                  ? semanticColors.textBody
                                  : semanticColors.textTertiary,
                              }}
                            >
                              {record.address_full || record.location_label}
                            </Typography>
                          )}
                        </Box>
                      </ButtonBase>
                      <IconButton
                        size="small"
                        disabled={addressSaving || showAddressEditor}
                        onClick={(event) => {
                          deleteTriggerRef.current = event.currentTarget;
                          focusHeadingRef.current = false;
                          setBallotToDelete(record);
                        }}
                        aria-label={tBallot('records.deleteAriaLabel', { name })}
                        sx={{
                          position: 'absolute',
                          insetInlineEnd: '4px',
                          color: semanticColors.textTertiary,
                          width: 36,
                          height: 36,
                          '&:hover': { color: semanticColors.danger },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  );
                }
              )}
            </Box>
            {visibleRecords.length > VISIBLE_BALLOT_ROWS && (
              <ButtonBase
                disableRipple
                onClick={() => {
                  setShowAllBallots((previous) => !previous);
                  if (!showAllBallots) analytics.track('Ballot History Expanded');
                }}
                aria-expanded={showAllBallots}
                aria-controls="settings-ballot-list"
                aria-label={
                  showAllBallots ? tBallot('records.showLessAria') : tBallot('records.showAllAria')
                }
                sx={{
                  width: '100%',
                  display: 'block',
                  textAlign: 'center',
                  px: 1,
                  py: 1,
                  borderRadius: BORDER_RADIUS,
                  '&:hover': { backgroundColor: semanticColors.overlaySubtle },
                  '&:focus-visible': {
                    outline: `2px solid ${primitiveColors.logoPurple}`,
                    outlineOffset: '-2px',
                  },
                }}
              >
                <Typography
                  sx={{ fontSize: '13px', fontWeight: 600, color: semanticColors.textBrand }}
                >
                  {showAllBallots ? tBallot('records.showLess') : tBallot('records.showAll')}
                </Typography>
              </ButtonBase>
            )}
          </>
        ) : null}

        {surface === 'dialog' ? (
          <Box sx={{ borderTop: `1px solid ${semanticColors.borderLight}`, mt: 1.5 }}>
            <SettingsRow
              label={t('autoSaveBallots')}
              description={t(autoSaveBallots ? 'autoSaveBallotsDesc' : 'autoSaveBallotsOffDesc')}
              descriptionId="settings-auto-save-desc"
              controlAlignment="start"
              divider={false}
              control={autoSaveControl}
              status={autoSaveStatus}
            />
          </Box>
        ) : (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pt: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                  {t('autoSaveBallots')}
                </Typography>
                <Typography
                  id="settings-auto-save-desc"
                  sx={{ fontSize: '13px', color: semanticColors.textTertiary }}
                >
                  {t('autoSaveBallotsDesc')}
                </Typography>
              </Box>
              {autoSaveControl}
            </Box>
            {autoSaveStatus}
          </>
        )}
      </SettingsSection>

      <Dialog
        open={Boolean(ballotToDelete)}
        onClose={closeDeleteDialog}
        disableEscapeKeyDown={isDeleting}
        disableRestoreFocus
        aria-labelledby="settings-delete-ballot-title"
        aria-describedby="settings-delete-ballot-body"
        slotProps={{ transition: { onExited: restoreDeleteFocus } }}
      >
        <DialogTitle id="settings-delete-ballot-title" sx={{ fontSize: '16px', fontWeight: 700 }}>
          {tBallot('records.deleteTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography id="settings-delete-ballot-body" sx={{ fontSize: '14px', mb: 2 }}>
            {tBallot('records.deleteBody')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              onClick={closeDeleteDialog}
              disabled={isDeleting}
              sx={{ textTransform: 'none' }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => void confirmDeleteBallot()}
              disabled={isDeleting}
              aria-busy={isDeleting}
              color="error"
              variant="contained"
              startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : undefined}
              sx={{ textTransform: 'none' }}
            >
              {tBallot('records.delete')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
